/**
 * Generates a random number between min and max (inclusive) using a cryptographically secure random function
 * @param min - Minimum value (inclusive), defaults to 0
 * @param max - Maximum value (inclusive), defaults to 10
 * @param rand - Random bytes generator function
 * @returns A random number between min and max
 */
export async function randomNumber(
    min: number = 0,
    max: number = 10,
    rand: (n: number) => Promise<Uint8Array>
): Promise<number> {
    let rval = 0;
    const range = max - min + 1;
    const bitsNeeded = Math.ceil(Math.log2(range));
    if (bitsNeeded > 53) {
        throw new Error("We cannot generate numbers larger than 53 bits.");
    }

    const bytesNeeded = Math.ceil(bitsNeeded / 8);
    const mask = Math.pow(2, bitsNeeded) - 1;

    // Fill a byte array with N random numbers
    const byteArray = await rand(bytesNeeded);

    let p = (bytesNeeded - 1) * 8;
    for (let i = 0; i < bytesNeeded; i++) {
        rval += byteArray[i] * Math.pow(2, p);
        p -= 8;
    }

    // Use & to apply the mask and reduce the number of recursive lookups
    // tslint:disable-next-line
    rval = rval & mask;

    if (rval >= range) {
        // Integer out of acceptable range
        return randomNumber(min, max, rand);
    }

    // Return an integer that falls within the range
    return min + rval;
}

/**
 * Formats a number with German locale formatting
 * @param val - The number to format
 * @param decimals - Number of decimal places, defaults to 2
 * @param thousandsSeparator - Whether to include thousands separator, defaults to true
 * @returns Formatted number string with comma as decimal separator and dot as thousands separator
 */
export function formatNumber(val: number, decimals = 2, thousandsSeparator = true): string {
    if (typeof val !== "number") {
        return formatNumber(0, decimals);
    }

    let res = val.toFixed(decimals).replace(".", ",");

    if (thousandsSeparator) {
        res = res.replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1.");
    }

    return res;
}

/**
 * Sums a specific property across an array of objects
 * @param items - Array of objects containing the property to sum
 * @param prop - The property key to sum
 * @returns The sum of all values for the specified property
 */
export function propSum<TKey extends PropertyKey>(items: Array<Record<TKey, number>>, prop: TKey): number {
    let total = 0;
    for (const item of items) {
        total += item[prop];
    }
    return total;
}

/**
 * Sums all numbers in an array
 * @param items - Array of numbers to sum
 * @returns The sum of all numbers in the array
 */
export function sum(items: number[]): number {
    let total = 0;
    for (const item of items) {
        total += item;
    }

    return total;
}

/**
 * Rounds a number to the specified number of decimal places
 * @param val - The number to round
 * @param decimals - Number of decimal places, defaults to 2
 * @returns The rounded number
 */
export function round(val: number, decimals = 2) {
    const factor = Math.pow(10, decimals);
    return Math.round(val * factor) / factor;
}

/**
 * Clamps a value between a minimum and maximum value
 * @param value - The value to clamp
 * @param min - The minimum allowed value
 * @param max - The maximum allowed value
 * @returns The clamped value
 */
export function clamp(value: number, min: number, max: number) {
    return Math.max(min, Math.min(max, value));
}